import { Link } from "react-router-dom";
import { ArrowLeft, Shield, Lock, UserCheck, Database, FileText } from "lucide-react";

export default function PrivacyPage() {
  return (
    <div data-testid="privacy-page" className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200/60">
        <div className="max-w-4xl mx-auto px-6 sm:px-8 py-6 flex items-center gap-4">
          <Link to="/" data-testid="privacy-back-link" className="text-slate-500 hover:text-slate-900 transition-colors">
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-lg bg-emerald-500 flex items-center justify-center">
              <Shield className="w-3.5 h-3.5 text-white" />
            </div>
            <span className="font-semibold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>CrediAcceso</span>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 sm:px-8 py-12">
        <h1
          data-testid="privacy-title"
          className="text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900 mb-8"
          style={{ fontFamily: 'Outfit, sans-serif' }}
        >
          Politica de Privacidad
        </h1>

        <div className="bg-white rounded-3xl border border-slate-200/60 shadow-sm p-8 md:p-12 space-y-8 text-sm text-slate-600 leading-relaxed">
          <p className="text-xs text-slate-400">Ultima actualizacion: Diciembre 2025</p>

          {/* Intro */}
          <section>
            <p className="text-base text-slate-700 leading-relaxed">
              Nuestra empresa, <strong>RECAUDACIONES Y COBRANZAS LIMITADA</strong>, protege la informacion personal
              siguiendo los estandares de la <strong>Ley N° 19.628</strong> sobre proteccion de la vida privada en Chile.
              Implementamos medidas de seguridad tecnicas y organizativas que incluyen:
            </p>
          </section>

          {/* Security measures cards */}
          <div className="grid sm:grid-cols-2 gap-5">
            <div data-testid="privacy-ssl" className="bg-slate-50 rounded-2xl p-6 border border-slate-100">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center">
                  <Lock className="w-5 h-5 text-emerald-600" />
                </div>
                <h3 className="text-base font-semibold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Protocolos de Seguridad (SSL)
                </h3>
              </div>
              <p>
                Nuestro sitio web cuenta con certificados SSL para cifrar la comunicacion entre el usuario y el servidor.
              </p>
            </div>

            <div data-testid="privacy-access" className="bg-slate-50 rounded-2xl p-6 border border-slate-100">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center">
                  <UserCheck className="w-5 h-5 text-emerald-600" />
                </div>
                <h3 className="text-base font-semibold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Acceso Restringido
                </h3>
              </div>
              <p>
                Solo el personal autorizado tiene acceso a los datos de los clientes, y estos se utilizan exclusivamente
                para los fines de asesoria financiera solicitados.
              </p>
            </div>

            <div data-testid="privacy-storage" className="bg-slate-50 rounded-2xl p-6 border border-slate-100">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center">
                  <Database className="w-5 h-5 text-emerald-600" />
                </div>
                <h3 className="text-base font-semibold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Almacenamiento Seguro
                </h3>
              </div>
              <p>
                La informacion se almacena en bases de datos protegidas con firewalls y sistemas de encriptacion
                para prevenir accesos no autorizados.
              </p>
            </div>

            <div data-testid="privacy-policies" className="bg-slate-50 rounded-2xl p-6 border border-slate-100">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center">
                  <FileText className="w-5 h-5 text-emerald-600" />
                </div>
                <h3 className="text-base font-semibold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Politicas de Privacidad Claras
                </h3>
              </div>
              <p>
                Los usuarios pueden consultar en cualquier momento nuestra politica de privacidad en el sitio web
                para conocer sus derechos de acceso, rectificacion y cancelacion de sus datos.
              </p>
            </div>
          </div>

          {/* Detailed sections */}
          <section>
            <h2 className="text-lg font-semibold text-slate-900 mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>1. Responsable del tratamiento</h2>
            <p>
              RECAUDACIONES Y COBRANZAS LIMITADA, RUT 96.754.900-2, con domicilio en Santo Domingo 628, Santiago,
              Region Metropolitana, Chile, es responsable del tratamiento de los datos personales recopilados a traves
              del sitio web crediacceso.cash.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-slate-900 mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>2. Datos que recopilamos</h2>
            <p className="mb-2">Recopilamos los siguientes datos personales proporcionados voluntariamente por el usuario:</p>
            <ul className="list-disc list-inside space-y-1 ml-2">
              <li>Nombre completo</li>
              <li>Correo electronico</li>
              <li>Numero de telefono</li>
              <li>Tipo de cliente (dependiente, independiente o empresa)</li>
              <li>Monto de credito solicitado</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-slate-900 mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>3. Finalidad del tratamiento</h2>
            <p className="mb-2">Los datos personales recopilados se utilizaran para:</p>
            <ul className="list-disc list-inside space-y-1 ml-2">
              <li>Evaluar opciones crediticias con entidades financieras asociadas</li>
              <li>Contactar al usuario con informacion sobre ofertas crediticias</li>
              <li>Mejorar nuestros servicios de intermediacion financiera</li>
              <li>Cumplir con obligaciones legales y regulatorias</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-slate-900 mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>4. Comparticion de datos</h2>
            <p>
              Los datos personales podran ser compartidos con entidades financieras asociadas con el unico proposito
              de evaluar la solicitud de credito del usuario. No vendemos ni compartimos datos personales con terceros
              para fines publicitarios o de marketing no relacionados con el servicio solicitado.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-slate-900 mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>5. Derechos del titular</h2>
            <p className="mb-2">De acuerdo con la Ley N° 19.628 sobre Proteccion de la Vida Privada, el usuario tiene derecho a:</p>
            <ul className="list-disc list-inside space-y-1 ml-2">
              <li>Acceder a sus datos personales</li>
              <li>Solicitar la rectificacion de datos inexactos</li>
              <li>Solicitar la eliminacion de sus datos cuando ya no sean necesarios</li>
              <li>Oponerse al tratamiento de sus datos en determinadas circunstancias</li>
            </ul>
            <p className="mt-2">
              Para ejercer estos derechos, puede contactarnos a traves del correo electronico contacto@crediacceso.cash.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-slate-900 mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>6. Retencion de datos</h2>
            <p>
              Los datos personales seran almacenados por el tiempo necesario para cumplir con las finalidades descritas
              en esta politica, o segun lo requiera la legislacion aplicable.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-slate-900 mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>7. Cookies</h2>
            <p>
              Este sitio web puede utilizar cookies y tecnologias similares para mejorar la experiencia del usuario.
              Las cookies nos permiten analizar el trafico del sitio y personalizar el contenido. El usuario puede
              configurar su navegador para rechazar cookies, aunque esto podria afectar la funcionalidad del sitio.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-slate-900 mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>8. Modificaciones</h2>
            <p>
              Nos reservamos el derecho de modificar esta politica de privacidad en cualquier momento. Las modificaciones
              seran publicadas en este sitio web con la fecha de actualizacion correspondiente.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-slate-900 mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>9. Contacto</h2>
            <p>
              Para consultas sobre esta politica de privacidad o el tratamiento de sus datos personales, contactenos:<br />
              Email: contacto@crediacceso.cash<br />
              Telefono: +56 2 2345 6789<br />
              Direccion: Santo Domingo 628, Santiago, Region Metropolitana, Chile
            </p>
          </section>
        </div>
      </div>
    </div>
  );
}
